import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDriverBookings } from "../../lib/driverApi";
import type { DriverBooking } from "../../lib/driverApi";
import { assetUrl, errorText } from "../../lib/api";
import { PageTitle, StatusBadge } from "../../components/company/ui";
import { cn, longDate } from "../../lib/format";
import { useT } from "../../i18n";

type Tab = "all" | "requests" | "upcoming" | "past";
const TABS: Record<Tab, DriverBooking["status"][]> = {
  all: ["pending", "documents_required", "confirmed", "active", "completed", "declined", "cancelled", "expired"],
  requests: ["pending", "documents_required"],
  upcoming: ["confirmed", "active"],
  past: ["completed", "declined", "cancelled", "expired"],
};

/** "My bookings": every booking, or pending requests, confirmed/active rentals and history. */
export default function DriverBookings() {
  const t = useT();
  const p = t.driverPages.bookings;
  const [bookings, setBookings] = useState<DriverBooking[] | null>(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<Tab>("all");

  useEffect(() => {
    getDriverBookings()
      .then(setBookings)
      .catch((err) => setError(errorText(err)));
  }, []);

  const rows = (bookings ?? []).filter((b) => TABS[tab].includes(b.status));

  return (
    <>
      <PageTitle>{p.title}</PageTitle>
      <div className="mb-4 flex flex-wrap gap-1.5" role="tablist">
        {(Object.keys(TABS) as Tab[]).map((k) => {
          const n = (bookings ?? []).filter((b) => TABS[k].includes(b.status)).length;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={cn(
                "h-8.5 cursor-pointer rounded-full border-0 px-3.5 text-[13px] font-medium",
                tab === k ? "bg-ink text-white" : "bg-white text-ink hover:bg-[#EDEDED]",
              )}
            >
              {p.tabs[k]} · {n}
            </button>
          );
        })}
      </div>

      {error ? (
        <p className="m-0 rounded-card bg-white p-6 text-sm text-tone-red-fg">{error}</p>
      ) : !bookings ? (
        <div className="h-40 animate-pulse rounded-card bg-white" aria-busy="true" />
      ) : rows.length === 0 ? (
        <div className="rounded-card bg-white px-6 py-12 text-center">
          <p className="m-0 mb-3 text-sm text-muted">{p.empty}</p>
          <Link to="/vehicles" className="text-sm text-brand-link">
            {p.find}
          </Link>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {rows.map((b) => (
            <Link
              key={b.id}
              to={`/driver/bookings/${b.ref}`}
              className="flex flex-wrap items-center gap-4 rounded-card bg-white p-4 text-ink no-underline hover:bg-[#FAFAFA]"
            >
              <div className="aspect-16/10 w-24 shrink-0 overflow-hidden rounded bg-linear-to-b from-surface to-[#E2E3E4]">
                {b.listing?.photo && <img src={assetUrl(b.listing.photo)} alt="" className="h-full w-full object-cover" />}
              </div>
              <div className="min-w-50 flex-1">
                <div className="text-base font-medium">{b.listing ? `${b.listing.brand} ${b.listing.model}` : b.ref}</div>
                <div className="mt-0.5 text-[13px] text-muted">
                  {[b.company?.name, b.listing?.city].filter(Boolean).join(" · ")}
                </div>
                <div className="mt-1 text-[13px]">
                  {longDate(b.start)} → {longDate(b.end)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <StatusBadge kind="booking" status={b.status} />
                <span className="font-mono text-xs text-faint">{b.ref}</span>
                {b.unread > 0 && <span className="text-xs font-medium text-brand">{p.unread(b.unread)}</span>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
